import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import {
  doc, getDoc, updateDoc, getDocs, addDoc, deleteDoc, collection, query, orderBy, where, onSnapshot, serverTimestamp, runTransaction
} from 'firebase/firestore';
import { deptLabel } from '../utils/depts';
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject
} from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, Grid, MenuItem, Avatar,
  Accordion, AccordionSummary, AccordionDetails, Table,
  TableHead, TableRow, TableCell, TableBody, Tooltip, ToggleButtonGroup, ToggleButton,
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Divider,
  FormControl, Select, Snackbar
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageIcon from '@mui/icons-material/Image';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import OfferPreview from '../components/OfferPreview';

/* ─── Content editor with bullet-insert button ───────────────────────────── */
function ContentEditor({ value, onChange, rows = 5 }) {
  const inputRef = useRef(null);
  const insertBullet = () => {
    const el = inputRef.current;
    if (!el) { onChange((value || '') + '\n- '); return; }
    const start = el.selectionStart;
    const before = (value || '').substring(0, start);
    const after  = (value || '').substring(el.selectionEnd);
    const lineStart = before.lastIndexOf('\n') + 1;
    const lineContent = before.substring(lineStart);
    let newVal, cursor;
    if (lineContent.trim() === '') {
      newVal = (value || '').substring(0, lineStart) + '- ' + (value || '').substring(lineStart);
      cursor = start + 2;
    } else {
      newVal = before + '\n- ' + after;
      cursor = start + 3;
    }
    onChange(newVal);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(cursor, cursor); });
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.25 }}>
        <Tooltip title="Lägg till punkt (börja rad med -)">
          <IconButton size="small" onClick={insertBullet}>
            <FormatListBulletedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField fullWidth multiline rows={rows} size="small" label="Innehåll"
        value={value || ''} onChange={e => onChange(e.target.value)} inputRef={inputRef} />
      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: 'block' }}>
        Börja en rad med <strong>-</strong> för punktlista
      </Typography>
    </Box>
  );
}

/* ─── Sortable section wrapper ───────────────────────────────────────────── */
function SortableSection({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Box ref={setNodeRef}
      style={{ transform: DndCSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      {children(listeners, attributes)}
    </Box>
  );
}

/* ─── Offer number generator ─────────────────────────────────────────────── */
async function generateOfferNumber(deptIds, departments) {
  const yy = String(new Date().getFullYear()).slice(-2);
  const year = new Date().getFullYear();
  const dept = departments.find(d => deptIds?.includes(d.id) && d.offerCode);
  if (!dept) return '';
  const counterId = `${dept.id}_${year}`;
  const counterRef = doc(db, 'offerCounters', counterId);
  const newCount = await runTransaction(db, async tx => {
    const snap = await tx.get(counterRef);
    const startAt = dept.offerStartNumber || 1;
    const current = snap.exists() ? snap.data().count : startAt - 1;
    const next = current + 1;
    tx.set(counterRef, { count: next, deptId: dept.id, year });
    return next;
  });
  return `${dept.offerCode}-${yy}-${String(newCount).padStart(3, '0')}`;
}

/* ─── Default sales texts ────────────────────────────────────────────────── */

const SALES_TEXTS = {
  laserskanning: `Vi tackar för er förfrågan och lämnar härmed offert avseende 3D-laserskanning samt anpassad bearbetning och leverans enligt projektets behov.

Rätt skanning – rätt nivå – rätt nytta
Laserskanning är ett kraftfullt verktyg. Den verkliga nyttan uppstår dock först när rätt information samlas in, i rätt omfattning och med tydlig koppling till projektets mål.

Tikab är strategisk partner inom digitalisering, BIM och informationsstyrning. Vi arbetar som beställarstöd och byggherrerådgivare i komplexa projekt där krav på precision, struktur och kvalitet är höga.

När ni beställer skanning genom oss får ni därför mer än en mätning – ni får en kvalitetssäkrad informationsleverans.

Vår styrka är att vi:
• Talar både beställarens och skanneroperatörens språk
• Förstår hur data ska användas i projektering, kalkyl, samordning och förvaltning
• Dimensionerar uppdraget så att ni får maximal projektnytta utan överleverans
• Säkerställer att resultatet fungerar direkt i era projekterings- och BIM-miljöer

Det minskar risken för omtag, felaktiga underlag och onödiga kostnader.

Erfarenhet från säkerhetsskyddsklassade miljöer
Vi har erfarenhet av arbete i miljöer med höga krav på informationssäkerhet och sekretess. Tillsammans med vår långsiktiga partner Exact är vi vana att hantera:
• Projekt med säkerhetsskyddsklassade förutsättningar
• Strukturerad hantering av skyddsvärd information
• Tydliga rutiner för åtkomst, lagring och överföring av data

Detta innebär att uppdrag kan genomföras tryggt även där krav på diskretion och kontroll är avgörande.

Genomförande och teknisk kapacitet
Skanningen utförs av Exact Svenska Mätcenter AB – en ledande aktör inom mätteknik och Scan to BIM. Med modern utrustning och lång erfarenhet från både små och mycket komplexa projekt säkerställs hög precision och effektiv datainsamling.

Tillsammans erbjuder vi:
• Noggrann 3D-laserskanning
• Punktmolnsleveranser anpassade för projektering
• Scan to BIM och modellering vid behov
• 360°-dokumentation och beslutsstöd

Kombinationen av teknisk spets och strategisk projektförståelse gör att leveransen inte bara blir korrekt – utan verkligt värdeskapande.

Att beställa skanning genom Tikab är därför en investering i bättre beslutsunderlag, säkrare projektering och ett mer effektivt projektgenomförande.`,
};

/* ─── Constants ──────────────────────────────────────────────────────────── */

const STATUS_META = {
  draft:    { label: 'Utkast',    color: '#78909C', bg: '#ECEFF1' },
  sent:     { label: 'Skickad',   color: '#1565C0', bg: '#E3F2FD' },
  accepted: { label: 'Accepterad',color: '#2E7D32', bg: '#E8F5E9' },
  declined: { label: 'Nekad',     color: '#C62828', bg: '#FFEBEE' },
  expired:  { label: 'Utgången',  color: '#795548', bg: '#EFEBE9' },
};

const SECTION_TYPES = [
  { key: 'intro',      label: 'Inledning' },
  { key: 'scope',      label: 'Omfattning' },
  { key: 'approach',   label: 'Genomförandebeskrivning' },
  { key: 'team',       label: 'Teamet' },
  { key: 'pricing',    label: 'Arvoden och villkor' },
  { key: 'budget',     label: 'Budgetsammanställning' },
  { key: 'delivery',   label: 'Leverans' },
  { key: 'terms',      label: 'Övrigt & Villkor' },
  { key: 'custom',     label: 'Fritt block' },
  { key: 'pagebreak',  label: '— Sidbrytning' },
  { key: 'spacer',     label: '↕ Utrymme (PDF)' },
];

const OFFER_TYPES = [
  { key: 'konsult-team', label: 'Konsultteam' },
  { key: 'konsult-cv',   label: 'Konsult-CV' },
  { key: 'fastpris',     label: 'Fastpris' },
  { key: 'lopande',      label: 'Löpande arvode' },
  { key: 'produkt',      label: 'Produkt/SaaS' },
  { key: 'kombination',  label: 'Kombination' },
];

const DEFAULT_TERMS = `Offert giltig till och med: [DATUM]
Startdatum: Enligt överenskommelse mellan Beställare och Tikab.
Samtliga priser är exklusive mervärdesskatt (moms).

Allmänna villkor
Det som inte beskrivs i den här offerten regleras enligt ABK 09.`;

function emptyPricingRow() {
  return { id: Date.now().toString() + Math.random(), description: '', price: '', unit: 'tim', note: '' };
}

function newSection(type) {
  const base = {
    id: Date.now().toString() + Math.random(),
    type,
    title: SECTION_TYPES.find(s => s.key === type)?.label || type,
    content: '',
  };
  if (type === 'pagebreak') return { ...base, title: 'Sidbrytning' };
  if (type === 'spacer')    return { ...base, title: 'Utrymme', height: 50 };
  if (type === 'pricing') return {
    ...base,
    pricingMode: 'hourly', // 'hourly' | 'fixed'
    rows: [emptyPricingRow()],
    budgetNote: '',
    paymentTerms: 'Månadsvis fakturering med redovisning av dagbok och nedlagd tid.',
  };
  if (type === 'team') return { ...base, userIds: [], content: '' };
  if (type === 'terms') return { ...base, content: DEFAULT_TERMS };
  if (type === 'budget') return { ...base, title: 'Budgetsammanställning', budgetId: '', budgetDisplay: 'activity' };
  return base;
}

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function OfferEditor() {
  const { offerId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [offer, setOffer] = useState(null);
  const [sections, setSections] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [companyLogos, setCompanyLogos] = useState({ logo: '', certLogo: '', name: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [meta, setMeta] = useState({});
  const [addSectionType, setAddSectionType] = useState('scope');
  const [departments, setDepartments] = useState([]);
  const [newCompanyDialog, setNewCompanyDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyContact, setNewCompanyContact] = useState('');
  const [newCompanyEmail, setNewCompanyEmail] = useState('');
  const [newCompanyPhone, setNewCompanyPhone] = useState('');
  const [clientContacts, setClientContacts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachUploadProgress, setAttachUploadProgress] = useState(null);
  const [libPickerOpen, setLibPickerOpen] = useState(false);
  const [libDocs, setLibDocs] = useState([]);
  const [libSearch, setLibSearch] = useState('');
  const [libCatFilter, setLibCatFilter] = useState('');
  const [libCategories, setLibCategories] = useState([]);
  const [libUploadDialog, setLibUploadDialog] = useState(false);
  const [libUploadFile, setLibUploadFile] = useState(null);
  const [libUploadName, setLibUploadName] = useState('');
  const [libUploadCat, setLibUploadCat] = useState('');
  const [libUploadProgress, setLibUploadProgress] = useState(null);
  const [splitPct, setSplitPct] = useState(50);
  const [viewStats, setViewStats] = useState(null);
  const [offerComments, setOfferComments] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const dragging = useRef(false);
  const containerRef = useRef(null);
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: meta.offerNumber || 'Offert',
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 20mm 0 10mm 0;
        @bottom-center {
          content: "Tikab Strukturmekanik AB | Björnstigen 87 | 170 73 Solna | +46 8 409 043 00 | info@tikab.com | Org.nr: 556246-6275";
          font-size: 7pt;
          color: #888;
          font-family: "Segoe UI", Arial, sans-serif;
        }
      }
      @page :first {
        margin-top: 0;
        margin-bottom: 0;
        @bottom-center { content: none; }
      }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #f0f0f0 !important; }
    `,
  });
  const [companyRefs, setCompanyRefs] = useState([]);
  const [refPickerOpen, setRefPickerOpen] = useState(false);
  const [selectedRefIds, setSelectedRefIds] = useState([]);
  const [refSearch, setRefSearch] = useState('');
  const [shareToken, setShareToken] = useState('');
  const [shareEnabled, setShareEnabled] = useState(false);
  const [clientResponse, setClientResponse] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [copySnack, setCopySnack] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    setLoading(true);
    const userSnap0 = await getDoc(doc(db, 'users', currentUser.uid));
    const companyId = userSnap0.data()?.companyId;

    const [offerSnap, compSnap, userSnap, orgSnap, refSnap, deptSnap] = await Promise.all([
      getDoc(doc(db, 'offers', offerId)),
      getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
      getDocs(collection(db, 'users')),
      companyId ? getDoc(doc(db, 'companies', companyId)) : Promise.resolve(null),
      getDocs(query(collection(db, 'referenceProjects'), orderBy('name'))),
      getDocs(collection(db, 'departments')),
    ]);
    if (!offerSnap.exists()) { navigate('/offers'); return; }
    const data = { id: offerSnap.id, ...offerSnap.data() };
    setOffer(data);
    setSections(data.sections || []);
    setShareToken(data.shareToken || '');
    setShareEnabled(data.shareEnabled || false);
    setClientResponse(data.clientResponse || null);
    setMeta({
      title: data.title || '',
      subtitle: data.subtitle || '',
      offerNumber: data.offerNumber || '',
      ourContactUserId: data.ourContactUserId || '',
      ourContact: data.ourContact || '',
      ourEmail: data.ourEmail || '',
      ourPhone: data.ourPhone || '',
      clientContact: data.clientContact || '',
      clientEmail: data.clientEmail || '',
      clientPhone: data.clientPhone || '',
      validUntil: data.validUntil || '',
      startDate: data.startDate || '',
      estimatedValue: data.estimatedValue || '',
      companyId: data.companyId || '',
      status: data.status || 'draft',
      type: data.type || 'konsult-team',
      departmentIds: data.departmentIds || [],
      ownerId: data.ownerId || '',
      introText: data.introText || '',
    });
    setSelectedRefIds(data.selectedRefIds || []);
    setAttachments(data.attachments || []);
    if (data.companyId) {
      const ctSnap = await getDocs(query(collection(db, 'crmCompanies', data.companyId, 'contacts'), orderBy('name')));
      setClientContacts(ctSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    const comps = compSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setCompanies(comps);
    setCompanyRefs(refSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    const allUsersData = userSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setUsers(allUsersData);

    // Auto-fill our contact from current user if this is a new offer (no contact set)
    if (!data.ourContact) {
      const me = allUsersData.find(u => u.id === currentUser.uid);
      if (me) {
        setMeta(m => ({
          ...m,
          ourContactUserId: me.id,
          ourContact: me.name || me.displayName || me.email || '',
          ourEmail: me.email || '',
          ourPhone: me.signatureMobile || me.mobile || '',
        }));
      }
    }

    if (orgSnap?.exists()) {
      const orgData = orgSnap.data();
      setCompanyLogos({ logo: orgData.logo || '', certLogo: orgData.certLogo || '', name: orgData.name || '' });
    } else {
      // Fallback: hämta första tillgängliga company om companyId saknas på användaren
      const allOrgs = await getDocs(collection(db, 'companies'));
      if (!allOrgs.empty) {
        const orgData = allOrgs.docs[0].data();
        setCompanyLogos({ logo: orgData.logo || '', certLogo: orgData.certLogo || '', name: orgData.name || '' });
      }
    }
    setLoading(false);
  }, [offerId, navigate, currentUser.uid]);

  useEffect(() => { load(); }, [load]);

  // Refresh offer.budgets when window regains focus (user may have edited budget in another tab)
  useEffect(() => {
    const handleFocus = async () => {
      const snap = await getDoc(doc(db, 'offers', offerId));
      if (snap.exists()) {
        setOffer(prev => ({ ...prev, budgets: snap.data().budgets || [] }));
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [offerId]);

  // Real-time comment thread
  useEffect(() => {
    if (!offerId) return;
    const unsub = onSnapshot(
      query(collection(db, 'offers', offerId, 'comments'), orderBy('createdAt', 'asc')),
      snap => setOfferComments(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [offerId]);

  // Load view stats whenever the share panel is relevant
  useEffect(() => {
    if (!offerId) return;
    getDocs(query(collection(db, 'offerViews'), where('offerId', '==', offerId)))
      .then(snap => {
        const views = snap.docs.map(d => d.data());
        if (!views.length) { setViewStats({ count: 0 }); return; }
        const lastView = views
          .filter(v => v.openedAt)
          .sort((a, b) => b.openedAt.toMillis() - a.openedAt.toMillis())[0];
        const durViews = views.filter(v => v.duration > 0);
        const avgDuration = durViews.length
          ? Math.round(durViews.reduce((s, v) => s + v.duration, 0) / durViews.length)
          : 0;
        setViewStats({
          count: views.length,
          lastAt: lastView?.openedAt?.toDate(),
          avgDuration,
        });
      })
      .catch(() => {});
  }, [offerId]);


  async function handleSave() {
    setSaving(true);
    await updateDoc(doc(db, 'offers', offerId), {
      ...meta,
      estimatedValue: meta.estimatedValue ? Number(meta.estimatedValue) : null,
      sections,
      selectedRefIds,
      attachments,
      updatedAt: new Date(),
    });
    setSaving(false);
    setDirty(false);
  }

  async function handleCreateCompany() {
    if (!newCompanyName.trim()) return;
    const data = {
      name: newCompanyName.trim(),
      createdAt: new Date(),
      status: 'prospect',
      ...(newCompanyContact.trim() && { contactName: newCompanyContact.trim() }),
      ...(newCompanyEmail.trim()   && { email: newCompanyEmail.trim() }),
      ...(newCompanyPhone.trim()   && { phone: newCompanyPhone.trim() }),
    };
    const docRef = await addDoc(collection(db, 'crmCompanies'), data);
    const newComp = { id: docRef.id, ...data };
    setCompanies(prev => [...prev, newComp].sort((a, b) => a.name.localeCompare(b.name)));
    setMeta(m => ({
      ...m,
      companyId: docRef.id,
      ...(data.contactName && { clientContact: data.contactName }),
      ...(data.email       && { clientEmail: data.email }),
      ...(data.phone       && { clientPhone: data.phone }),
    }));
    // Store contact in subcollection if provided
    if (data.contactName) {
      await addDoc(collection(db, 'crmCompanies', docRef.id, 'contacts'), {
        name: data.contactName, email: data.email || '', phone: data.phone || '',
        primary: true, createdAt: new Date(),
      });
      setClientContacts([{ id: 'new', name: data.contactName, email: data.email || '', phone: data.phone || '' }]);
    }
    markDirty();
    setNewCompanyName(''); setNewCompanyContact(''); setNewCompanyEmail(''); setNewCompanyPhone('');
    setNewCompanyDialog(false);
  }

  async function handleAttachUpload(file) {
    if (!file) return;
    const path = `offers/${offerId}/attachments/${Date.now()}_${file.name}`;
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, file);
    setAttachUploadProgress(0);
    const url = await new Promise((resolve, reject) => {
      task.on('state_changed',
        snap => setAttachUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        reject,
        async () => resolve(await getDownloadURL(task.snapshot.ref))
      );
    });
    const newAtt = { id: crypto.randomUUID(), name: file.name, url, fileName: file.name, source: 'upload', storagePath: path };
    const updated = [...attachments, newAtt];
    setAttachments(updated);
    await updateDoc(doc(db, 'offers', offerId), { attachments: updated });
    setAttachUploadProgress(null);
  }

  async function handleAttachFromLibrary(libDoc) {
    if (attachments.some(a => a.libDocId === libDoc.id)) return; // already added
    const newAtt = { id: crypto.randomUUID(), name: libDoc.name, url: libDoc.currentFileUrl, fileName: libDoc.currentFileName, source: 'library', libDocId: libDoc.id };
    const updated = [...attachments, newAtt];
    setAttachments(updated);
    await updateDoc(doc(db, 'offers', offerId), { attachments: updated });
  }

  async function handleRemoveAttachment(att) {
    if (att.source === 'upload' && att.storagePath) {
      try { await deleteObject(storageRef(storage, att.storagePath)); } catch (_) {}
    }
    const updated = attachments.filter(a => a.id !== att.id);
    setAttachments(updated);
    await updateDoc(doc(db, 'offers', offerId), { attachments: updated });
  }

  async function openLibPicker() {
    const [snap, settingsSnap] = await Promise.all([
      libDocs.length === 0 ? getDocs(collection(db, 'documentLibrary')) : Promise.resolve(null),
      libCategories.length === 0 ? getDoc(doc(db, 'settings', 'documentLibrary')) : Promise.resolve(null),
    ]);
    if (snap) {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.category || '').localeCompare(b.category || ''));
      setLibDocs(docs);
      // Derive categories from actual docs as fallback
      if (!settingsSnap?.exists()) {
        setLibCategories([...new Set(docs.map(d => d.category).filter(Boolean))].sort());
      }
    }
    if (settingsSnap?.exists() && settingsSnap.data().categories?.length) {
      setLibCategories(settingsSnap.data().categories);
    }
    setLibPickerOpen(true);
  }

  async function handleUploadToLibrary() {
    if (!libUploadFile || !libUploadName.trim()) return;
    setLibUploadProgress(0);
    const path = `documentLibrary/${Date.now()}_${libUploadFile.name}`;
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, libUploadFile);
    const fileUrl = await new Promise((resolve, reject) => {
      task.on('state_changed',
        snap => setLibUploadProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        reject,
        async () => resolve(await getDownloadURL(task.snapshot.ref))
      );
    });
    const docRef = await addDoc(collection(db, 'documentLibrary'), {
      name: libUploadName.trim(),
      category: libUploadCat || 'Övrigt',
      currentVersion: 1, currentFileUrl: fileUrl, currentFileName: libUploadFile.name,
      createdAt: new Date(), updatedAt: new Date(),
    });
    const newDoc = { id: docRef.id, name: libUploadName.trim(), category: libUploadCat || 'Övrigt', currentFileUrl: fileUrl, currentFileName: libUploadFile.name };
    setLibDocs(prev => [...prev, newDoc].sort((a, b) => (a.category || '').localeCompare(b.category || '')));
    // Also add as attachment immediately
    handleAttachFromLibrary(newDoc);
    setLibUploadDialog(false); setLibUploadFile(null); setLibUploadName(''); setLibUploadCat('');
    setLibUploadProgress(null); setLibPickerOpen(false); setLibSearch(''); setLibCatFilter('');
  }

  async function toggleShare() {
    const newEnabled = !shareEnabled;
    let token = shareToken;
    if (newEnabled && !token) {
      token = crypto.randomUUID();
      setShareToken(token);
    }
    setShareEnabled(newEnabled);
    await updateDoc(doc(db, 'offers', offerId), {
      shareEnabled: newEnabled,
      ...(token && !shareToken ? { shareToken: token } : {}),
    });
  }

  function copyShareLink() {
    const url = `${window.location.origin}/offer/${shareToken}`;
    navigator.clipboard.writeText(url);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  }

  async function setStatus(newStatus) {
    const update = { status: newStatus, updatedAt: new Date() };
    if (newStatus === 'sent') update.sentAt = new Date();
    await updateDoc(doc(db, 'offers', offerId), update);
    setMeta(m => ({ ...m, status: newStatus }));
    setOffer(o => ({ ...o, status: newStatus }));
  }

  function markDirty() { setDirty(true); }
  function setMetaField(field, value) { setMeta(m => ({ ...m, [field]: value })); markDirty(); }

  function updateSection(id, field, value) {
    setSections(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
    markDirty();
  }

  function removeSection(id) {
    setSections(prev => prev.filter(s => s.id !== id));
    markDirty();
  }

  function addSection() {
    setSections(prev => [...prev, newSection(addSectionType)]);
    markDirty();
  }

  function updateRow(secId, rowId, field, value) {
    setSections(prev => prev.map(s => {
      if (s.id !== secId) return s;
      return { ...s, rows: s.rows.map(r => r.id === rowId ? { ...r, [field]: value } : r) };
    }));
    markDirty();
  }

  function addRow(secId) {
    setSections(prev => prev.map(s =>
      s.id === secId ? { ...s, rows: [...(s.rows || []), emptyPricingRow()] } : s
    ));
    markDirty();
  }

  function removeRow(secId, rowId) {
    setSections(prev => prev.map(s =>
      s.id === secId ? { ...s, rows: s.rows.filter(r => r.id !== rowId) } : s
    ));
    markDirty();
  }

  function toggleUser(secId, userId) {
    setSections(prev => prev.map(s => {
      if (s.id !== secId) return s;
      const ids = s.userIds || [];
      return {
        ...s,
        userIds: ids.includes(userId) ? ids.filter(id => id !== userId) : [...ids, userId]
      };
    }));
    markDirty();
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 12 }}>
      <CircularProgress />
    </Box>
  );
  if (!offer) return null;

  const st = STATUS_META[meta.status] || STATUS_META.draft;
  const companyObj = companies.find(c => c.id === meta.companyId);

  return (
    <>
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100 }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/offers')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {meta.offerNumber && (
                <Typography variant="caption"
                  sx={{ color: '#FF6B00', fontWeight: 700 }}>
                  {meta.offerNumber}
                </Typography>
              )}
              <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ maxWidth: 300 }}>
                {meta.title || 'Namnlös offert'}
              </Typography>
            </Box>
            <Chip label={st.label} size="small"
              sx={{ bgcolor: st.bg, color: st.color, fontWeight: 700,
                height: 18, fontSize: '0.6rem' }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {meta.status === 'draft' && (
            <Button size="small" startIcon={<SendIcon />}
              onClick={() => setStatus('sent')}
              sx={{ color: '#90CAF9', borderColor: '#90CAF9', border: '1px solid' }}>
              Markera skickad
            </Button>
          )}
          {meta.status === 'sent' && (
            <>
              <Button size="small" startIcon={<CheckCircleIcon />}
                onClick={() => setStatus('accepted')}
                sx={{ color: '#A5D6A7', borderColor: '#A5D6A7', border: '1px solid' }}>
                Accepterad
              </Button>
              <Button size="small" startIcon={<CancelIcon />}
                onClick={() => setStatus('declined')}
                sx={{ color: '#EF9A9A', borderColor: '#EF9A9A', border: '1px solid' }}>
                Nekad
              </Button>
            </>
          )}
          <Tooltip title={shareEnabled ? 'Kopierad! Länk aktiv' : 'Dela med kund'}>
            <IconButton
              size="small"
              onClick={async () => {
                if (!shareEnabled) {
                  await toggleShare();
                } else {
                  await navigator.clipboard.writeText(`${window.location.origin}/offer/${shareToken}`);
                  setCopySnack(true);
                }
              }}
              sx={{ color: shareEnabled ? '#64B5F6' : 'rgba(255,255,255,0.6)' }}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Skriv ut / Spara PDF">
            <IconButton color="inherit" size="small" onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Button size="small" variant="contained" startIcon={<SaveIcon />}
            disabled={saving || !dirty} onClick={handleSave}
            sx={{ bgcolor: dirty ? '#FF6B00' : '#555',
              '&:hover': { bgcolor: '#e55a00' } }}>
            {saving ? 'Sparar...' : dirty ? 'Spara' : 'Sparat'}
          </Button>
        </Box>
      </Box>

      {/* Two-column editor + preview */}
      <Box ref={containerRef} sx={{ display: 'flex', height: 'calc(100vh - 56px)' }} className="no-print"
        onMouseMove={e => {
          if (!dragging.current || !containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          const pct = Math.min(80, Math.max(20, ((e.clientX - rect.left) / rect.width) * 100));
          setSplitPct(pct);
        }}
        onMouseUp={() => { dragging.current = false; document.body.style.cursor = ''; }}
        onMouseLeave={() => { dragging.current = false; document.body.style.cursor = ''; }}
      >

        {/* ── Left: Editor ── */}
        <Box sx={{ width: `${splitPct}%`, overflowY: 'auto', p: 2.5, flexShrink: 0 }}>

          {/* Meta */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Offertinformation
              </Typography>
              {(() => {
                const hasBudget = (offer?.budgets || []).some(b =>
                  (b.resources?.length > 0) || (b.activities?.length > 0));
                return hasBudget ? (
                  <Tooltip title={`${(offer?.budgets || []).length} budgetversion(er) kopplad`}>
                    <Button size="small" variant="outlined" startIcon={<AccountBalanceWalletIcon />}
                      onClick={() => navigate(`/offers/${offerId}/budget-edit`)}
                      sx={{ color: '#9C27B0', borderColor: '#CE93D8',
                        '&:hover': { borderColor: '#9C27B0', bgcolor: 'rgba(206,147,216,0.1)' } }}>
                      Budget
                      <Box component="span" sx={{
                        ml: 0.8, bgcolor: '#9C27B0', color: 'white',
                        borderRadius: '10px', fontSize: '0.6rem', fontWeight: 700,
                        px: 0.7, py: 0.1, lineHeight: 1.6,
                      }}>
                        {(offer?.budgets || []).length}
                      </Box>
                    </Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="Ingen budget kopplad — klicka för att skapa">
                    <Button size="small" variant="outlined" startIcon={<AccountBalanceWalletIcon />}
                      onClick={() => navigate(`/offers/${offerId}/budget-edit`)}
                      sx={{
                        color: '#FF6B00', borderColor: '#FF6B00', borderStyle: 'dashed',
                        '&:hover': { borderStyle: 'solid', bgcolor: 'rgba(255,107,0,0.08)' },
                      }}>
                      Koppla budget
                    </Button>
                  </Tooltip>
                );
              })()}
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={4}>
                <TextField fullWidth label="Offertnummer (t.ex. BTM1251)" size="small"
                  value={meta.offerNumber || ''}
                  onChange={e => setMetaField('offerNumber', e.target.value)}
                  placeholder="BTM1251" />
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth label="Status" size="small"
                  value={meta.status || 'draft'}
                  onChange={e => setMetaField('status', e.target.value)}>
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField select fullWidth label="Typ" size="small"
                  value={meta.type || 'konsult-team'}
                  onChange={e => setMetaField('type', e.target.value)}>
                  {OFFER_TYPES.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={8}>
                <TextField select fullWidth label="Affärsområde" size="small"
                  value={meta.departmentIds?.[0] || ''}
                  onChange={async e => {
                    const deptId = e.target.value;
                    const newDeptIds = deptId ? [deptId] : [];
                    setMetaField('departmentIds', newDeptIds);
                    if (deptId && !meta.offerNumber) {
                      const num = await generateOfferNumber(newDeptIds, departments);
                      if (num) {
                        setMeta(m => ({ ...m, departmentIds: newDeptIds, offerNumber: num }));
                        await updateDoc(doc(db, 'offers', offerId), { offerNumber: num, departmentIds: newDeptIds });
                      }
                    }
                  }}>
                  <MenuItem value="">– Välj affärsområde –</MenuItem>
                  {departments.map(d => <MenuItem key={d.id} value={d.id}>{deptLabel(d)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Offerttitel (rubrik)" size="small"
                  value={meta.title || ''}
                  onChange={e => setMetaField('title', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Undertitel / projektnamn (orange i preview)"
                  size="small" value={meta.subtitle || ''}
                  onChange={e => setMetaField('subtitle', e.target.value)}
                  placeholder="t.ex. Programhandlingsskede Kv 15 - Wave" />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontWeight: 600 }}>
                    Säljande text (förstasidan)
                  </Typography>
                  <Button size="small" variant="outlined"
                    onClick={() => setMetaField('introText', SALES_TEXTS.laserskanning)}
                    sx={{ fontSize: '0.65rem', py: 0.3, px: 1,
                      borderColor: '#FF6B00', color: '#FF6B00' }}>
                    Fyll i: Laserskanning & modellering
                  </Button>
                </Box>
                <TextField fullWidth size="small"
                  multiline rows={10}
                  value={meta.introText || ''}
                  onChange={e => setMetaField('introText', e.target.value)}
                  placeholder="Vi tackar för er förfrågan och lämnar härmed offert avseende..." />
              </Grid>
            </Grid>
          </Paper>

          {/* Parter */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
              Parter & kontakt
            </Typography>
            <Grid container spacing={1.5}>
              {/* Vår part: företagsnamn, read-only */}
              <Grid item xs={12}>
                <TextField fullWidth label="Vår part (företag)" size="small"
                  value={companyLogos.name || 'Tikab Strukturmekanik AB'}
                  InputProps={{ readOnly: true }}
                  sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }} />
              </Grid>
              {/* Vår ref: välj medarbetare */}
              <Grid item xs={12}>
                <TextField select fullWidth label="Vår ref (medarbetare)" size="small"
                  value={meta.ourContactUserId || ''}
                  onChange={e => {
                    const u = users.find(u => u.id === e.target.value);
                    if (u) {
                      setMeta(m => ({
                        ...m,
                        ourContactUserId: u.id,
                        ourContact: u.name || u.displayName || u.email || '',
                        ourEmail: u.email || '',
                        ourPhone: u.signatureMobile || u.mobile || '',
                      }));
                      markDirty();
                    }
                  }}>
                  <MenuItem value="">– Välj medarbetare –</MenuItem>
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.name || u.displayName || u.email}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Namn" size="small"
                  value={meta.ourContact || ''}
                  onChange={e => setMetaField('ourContact', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="E-post" size="small"
                  value={meta.ourEmail || ''}
                  onChange={e => setMetaField('ourEmail', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Telefon" size="small"
                  value={meta.ourPhone || ''}
                  onChange={e => setMetaField('ourPhone', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField select fullWidth label="Kund/företag" size="small"
                    value={meta.companyId || ''}
                    onChange={async e => {
                      const newCompanyId = e.target.value;
                      setMeta(m => ({ ...m, companyId: newCompanyId, clientContact: '', clientEmail: '', clientPhone: '' }));
                      markDirty();
                      setClientContacts([]);
                      if (newCompanyId) {
                        const ctSnap = await getDocs(query(collection(db, 'crmCompanies', newCompanyId, 'contacts'), orderBy('name')));
                        const cts = ctSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                        setClientContacts(cts);
                        if (cts.length === 1) {
                          setMeta(m => ({ ...m, clientContact: cts[0].name || '', clientEmail: cts[0].email || '', clientPhone: cts[0].phone || '' }));
                        }
                      }
                    }}>
                    <MenuItem value="">– Välj företag –</MenuItem>
                    {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                  </TextField>
                  <Tooltip title="Skapa nytt företag">
                    <IconButton size="small" onClick={() => setNewCompanyDialog(true)}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.3 }}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              {clientContacts.length > 1 && (
                <Grid item xs={12}>
                  <TextField select fullWidth label="Välj kontaktperson" size="small"
                    value={meta.clientContact || ''}
                    onChange={e => {
                      const ct = clientContacts.find(c => c.name === e.target.value);
                      setMeta(m => ({ ...m, clientContact: ct?.name || '', clientEmail: ct?.email || '', clientPhone: ct?.phone || '' }));
                      markDirty();
                    }}>
                    <MenuItem value="">– Välj kontakt –</MenuItem>
                    {clientContacts.map(ct => (
                      <MenuItem key={ct.id} value={ct.name}>
                        {ct.name}{ct.role ? ` — ${ct.role}` : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField fullWidth label="Er part (kontaktperson hos kund)" size="small"
                  value={meta.clientContact || ''}
                  onChange={e => setMetaField('clientContact', e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="E-post (kund)" size="small"
                  value={meta.clientEmail || ''}
                  onChange={e => setMetaField('clientEmail', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Telefon (kund)" size="small"
                  value={meta.clientPhone || ''}
                  onChange={e => setMetaField('clientPhone', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Offert giltig t.o.m." size="small"
                  type="date" InputLabelProps={{ shrink: true }}
                  value={meta.validUntil || ''}
                  onChange={e => setMetaField('validUntil', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth label="Estimerat värde (SEK)" size="small"
                  type="number" value={meta.estimatedValue || ''}
                  onChange={e => setMetaField('estimatedValue', e.target.value)} />
              </Grid>
            </Grid>
          </Paper>

          {/* Referensuppdrag */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Referensuppdrag
              </Typography>
              <Button size="small" startIcon={<AddIcon />}
                onClick={() => setRefPickerOpen(true)}
                sx={{ borderColor: '#FF6B00', color: '#FF6B00', border: '1px solid' }}>
                Välj referens
              </Button>
            </Box>
            {selectedRefIds.length === 0 && (
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                Inga referensuppdrag valda ännu
              </Typography>
            )}
            {companyRefs.filter(r => selectedRefIds.includes(r.id)).map(ref => (
              <Box key={ref.id} sx={{ display: 'flex', alignItems: 'flex-start',
                justifyContent: 'space-between', py: 0.8,
                borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{ref.name}</Typography>
                    {ref.projectNumber && (
                      <Typography variant="caption" color="text.disabled">#{ref.projectNumber}</Typography>
                    )}
                  </Box>
                  {ref.client && (
                    <Typography variant="caption" color="text.secondary">{ref.client}</Typography>
                  )}
                  {(ref.startDate || ref.endDate) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {ref.startDate}{ref.endDate ? ' – ' + ref.endDate : ''}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small"
                  onClick={() => { setSelectedRefIds(prev => prev.filter(id => id !== ref.id)); markDirty(); }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Paper>

          {/* Bilagor */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Bilagor ({attachments.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Välj från DokumentN">
                  <Button size="small" startIcon={<FolderOpenIcon />}
                    onClick={openLibPicker}
                    sx={{ fontSize: '0.7rem', borderColor: '#FF6B00', color: '#FF6B00', border: '1px solid' }}>
                    Bibliotek
                  </Button>
                </Tooltip>
                <Tooltip title="Ladda upp fil">
                  <Button size="small" component="label" startIcon={<CloudUploadIcon />}
                    sx={{ fontSize: '0.7rem', borderColor: 'divider', color: 'text.secondary', border: '1px solid' }}>
                    Ladda upp
                    <input type="file" hidden accept=".pdf,.doc,.docx,.xlsx,.xls,.ppt,.pptx,.png,.jpg,.jpeg"
                      onChange={e => { if (e.target.files[0]) handleAttachUpload(e.target.files[0]); e.target.value = ''; }} />
                  </Button>
                </Tooltip>
              </Box>
            </Box>

            {attachUploadProgress !== null && (
              <LinearProgress variant="determinate" value={attachUploadProgress} sx={{ mb: 1, borderRadius: 1 }} />
            )}

            {attachments.length === 0 && attachUploadProgress === null ? (
              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                Inga bilagor ännu — ladda upp eller välj från DokumentN
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {attachments.map(att => (
                  <Box key={att.id} sx={{ display: 'flex', alignItems: 'center', gap: 1,
                    p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
                    <InsertDriveFileIcon sx={{ fontSize: 16, color: att.source === 'library' ? '#FF6B00' : 'text.secondary', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.name}
                    </Typography>
                    {att.source === 'library' && (
                      <Chip label="Bibliotek" size="small" sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#FFF3E0', color: '#FF6B00' }} />
                    )}
                    <Tooltip title="Öppna">
                      <IconButton size="small" component="a" href={att.url} target="_blank" rel="noopener noreferrer">
                        <OpenInNewIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => handleRemoveAttachment(att)} sx={{ color: 'error.main', opacity: 0.6 }}>
                      <DeleteIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Dela med kund */}
          <Paper sx={{ p: 2, mb: 2, border: shareEnabled ? '1px solid #1565C0' : undefined }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: shareEnabled ? 1.5 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                  Dela med kund
                </Typography>
                {clientResponse?.status && (
                  <Chip size="small"
                    label={clientResponse.status === 'approved' ? '✓ Godkänd av kund' : '💬 Kunden har kommenterat'}
                    sx={{
                      bgcolor: clientResponse.status === 'approved' ? '#E8F5E9' : '#E3F2FD',
                      color:   clientResponse.status === 'approved' ? '#2E7D32' : '#1565C0',
                      fontWeight: 700, fontSize: '0.65rem',
                    }} />
                )}
              </Box>
              <Button size="small" variant={shareEnabled ? 'contained' : 'outlined'}
                onClick={toggleShare}
                sx={shareEnabled
                  ? { bgcolor: '#1565C0', '&:hover': { bgcolor: '#0d47a1' }, fontSize: '0.72rem' }
                  : { borderColor: '#1565C0', color: '#1565C0', fontSize: '0.72rem' }}>
                {shareEnabled ? 'Länk aktiv' : 'Aktivera länk'}
              </Button>
            </Box>

            {shareEnabled && shareToken && (
              <Box>
                <Box sx={{ bgcolor: '#E3F2FD', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                  <Typography variant="caption" color="#1565C0" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                    Så här fungerar det:
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    1. Kopiera länken nedan och skicka till kunden via mail<br />
                    2. Kunden öppnar länken — <strong>ingen inloggning krävs</strong><br />
                    3. Kunden kan <strong>godkänna offerten</strong> eller lämna synpunkter<br />
                    4. Svaret visas direkt här och offertens status uppdateras automatiskt
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField fullWidth size="small"
                    value={`${window.location.origin}/offer/${shareToken}`}
                    InputProps={{ readOnly: true, sx: { fontSize: '0.75rem', bgcolor: '#fafafa' } }} />
                  <Button size="small" variant="outlined" onClick={copyShareLink}
                    sx={{ whiteSpace: 'nowrap', minWidth: 90,
                      borderColor: shareCopied ? '#2E7D32' : undefined,
                      color: shareCopied ? '#2E7D32' : undefined }}>
                    {shareCopied ? '✓ Kopierat' : 'Kopiera'}
                  </Button>
                </Box>
                {/* Comment thread */}
                {(offerComments.length > 0 || clientResponse?.status) && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 1 }}>
                      Kommentarer
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5, maxHeight: 300, overflowY: 'auto' }}>
                      {offerComments.map(c => {
                        const isClient = c.side === 'client';
                        return (
                          <Box key={c.id} sx={{ display: 'flex', justifyContent: isClient ? 'flex-start' : 'flex-end' }}>
                            <Box sx={{
                              maxWidth: '85%',
                              bgcolor: isClient ? '#EFF6FF' : '#FFF7ED',
                              border: `1px solid ${isClient ? '#BFDBFE' : '#FED7AA'}`,
                              borderRadius: isClient ? '3px 8px 8px 8px' : '8px 3px 8px 8px',
                              p: '6px 10px',
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mb: 0.2 }}>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700,
                                  color: isClient ? '#1e40af' : '#c2410c' }}>
                                  {c.authorName || (isClient ? 'Kund' : 'Tikab')}
                                </Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: '#bbb' }}>
                                  {c.createdAt?.toDate?.()?.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' }) || ''}
                                </Typography>
                                <Box sx={{ ml: 'auto', cursor: 'pointer', opacity: 0.3,
                                  '&:hover': { opacity: 1 }, display: 'flex' }}
                                  onClick={() => deleteDoc(doc(db, 'offers', offerId, 'comments', c.id))}>
                                  <DeleteIcon sx={{ fontSize: 11, color: 'error.main' }} />
                                </Box>
                              </Box>
                              <Typography sx={{ fontSize: '0.75rem', color: '#1a1a1a', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                                {c.text}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {/* Reply input */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mt: 1 }}>
                  <TextField fullWidth size="small" multiline maxRows={4}
                    placeholder="Svara kunden..."
                    value={replyText} onChange={e => setReplyText(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && replyText.trim()) {
                        setSendingReply(true);
                        await addDoc(collection(db, 'offers', offerId, 'comments'), {
                          text: replyText.trim(),
                          side: 'internal',
                          authorName: users.find(u => u.id === currentUser.uid)?.displayName || currentUser.email || 'Tikab',
                          createdAt: serverTimestamp(),
                        });
                        setReplyText('');
                        setSendingReply(false);
                      }
                    }}
                    InputProps={{ sx: { fontSize: '0.8rem' } }} />
                  <Button size="small" variant="contained" disabled={!replyText.trim() || sendingReply}
                    onClick={async () => {
                      if (!replyText.trim()) return;
                      setSendingReply(true);
                      await addDoc(collection(db, 'offers', offerId, 'comments'), {
                        text: replyText.trim(),
                        side: 'internal',
                        authorName: users.find(u => u.id === currentUser.uid)?.displayName || currentUser.email || 'Tikab',
                        createdAt: serverTimestamp(),
                      });
                      setReplyText('');
                      setSendingReply(false);
                    }}
                    sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, minWidth: 40, px: 1.5, alignSelf: 'flex-end' }}>
                    <SendIcon sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              </Box>
            )}

            {/* View stats */}
            {viewStats && viewStats.count > 0 && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: '#f5f5f5',
                display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                  <VisibilityIcon sx={{ fontSize: 14, color: '#888' }} />
                  <Typography variant="caption" fontWeight={700} color="text.primary">
                    {viewStats.count} visning{viewStats.count !== 1 ? 'ar' : ''}
                  </Typography>
                </Box>
                {viewStats.lastAt && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: '#888' }} />
                    <Typography variant="caption" color="text.secondary">
                      Senast {viewStats.lastAt.toLocaleDateString('sv-SE')}
                    </Typography>
                  </Box>
                )}
                {viewStats.avgDuration > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Snittid {viewStats.avgDuration >= 60
                      ? `${Math.floor(viewStats.avgDuration / 60)} min`
                      : `${viewStats.avgDuration} sek`}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Sections */}
          <DndContext sensors={sensors} collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const oldIdx = sections.findIndex(s => s.id === active.id);
              const newIdx = sections.findIndex(s => s.id === over.id);
              setSections(arrayMove(sections, oldIdx, newIdx));
              markDirty();
            }}>
            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          {sections.map((sec, idx) => (
            <SortableSection key={sec.id} id={sec.id}>
              {(dragListeners, dragAttributes) => sec.type === 'pagebreak' ? (
                <Box sx={{
                  mb: 1.5, display: 'flex', alignItems: 'center', gap: 1,
                  border: '1px dashed', borderColor: '#FF6B00', borderRadius: 1, p: 1, px: 1.5,
                  bgcolor: '#FFF8F0',
                }}>
                  <Box {...dragListeners} {...dragAttributes} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                    <DragIndicatorIcon sx={{ color: '#FF6B00', fontSize: 18 }} />
                  </Box>
                  <InsertPageBreakIcon sx={{ color: '#FF6B00', fontSize: 18 }} />
                  <Typography variant="caption" sx={{ color: '#FF6B00', fontWeight: 700, flex: 1 }}>
                    SIDBRYTNING
                  </Typography>
                  <IconButton size="small" onClick={() => removeSection(sec.id)} sx={{ color: 'error.main', opacity: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ) : sec.type === 'spacer' ? (
                <Box sx={{
                  mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
                  border: '1px dashed #9C27B0', borderRadius: 1, p: 1, px: 1.5,
                  bgcolor: '#F3E5F5',
                }}>
                  <Box {...dragListeners} {...dragAttributes} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                    <DragIndicatorIcon sx={{ color: '#9C27B0', fontSize: 18 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#9C27B0', fontSize: '1rem' }}>↕</Typography>
                  <Typography variant="caption" sx={{ color: '#9C27B0', fontWeight: 700, flex: 1 }}>
                    UTRYMME (PDF)
                  </Typography>
                  <TextField
                    type="number" size="small" variant="outlined"
                    value={sec.height || 50}
                    onChange={e => { updateSection(sec.id, 'height', Math.max(5, Math.min(200, Number(e.target.value)))); markDirty(); }}
                    inputProps={{ min: 5, max: 200, step: 5 }}
                    sx={{ width: 90 }}
                    label="mm"
                  />
                  <IconButton size="small" onClick={() => removeSection(sec.id)} sx={{ color: 'error.main', opacity: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ) : (
              <Accordion defaultExpanded disableGutters elevation={1}
                sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Box {...dragListeners} {...dragAttributes}
                    sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}
                    onClick={e => e.stopPropagation()}>
                    <DragIndicatorIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    {String(idx + 1).padStart(2, '0')} | {sec.title}
                  </Typography>
                  <Chip
                    label={SECTION_TYPES.find(s => s.key === sec.type)?.label || sec.type}
                    size="small" sx={{ fontSize: '0.6rem', height: 18 }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}
                  onClick={e => e.stopPropagation()}>
                  <Tooltip title={sec.pageBreakBefore
                    ? 'Sidbrytning före sektion (aktiv)'
                    : 'Lägg till sidbrytning före sektion'}>
                    <IconButton size="small"
                      onClick={() => updateSection(sec.id, 'pageBreakBefore', !sec.pageBreakBefore)}
                      sx={{ color: sec.pageBreakBefore ? '#FF6B00' : 'text.disabled' }}>
                      <InsertPageBreakIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => removeSection(sec.id)}
                    sx={{ color: 'error.main', opacity: 0.5 }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TextField fullWidth label="Sektionsrubrik" size="small" sx={{ mb: 1.5 }}
                  value={sec.title}
                  onChange={e => updateSection(sec.id, 'title', e.target.value)} />

                {sec.type === 'pricing' ? (
                  <PricingEditor section={sec}
                    onUpdateSection={(field, val) => updateSection(sec.id, field, val)}
                    onUpdateRow={(rowId, field, val) => updateRow(sec.id, rowId, field, val)}
                    onAddRow={() => addRow(sec.id)}
                    onRemoveRow={(rowId) => removeRow(sec.id, rowId)} />
                ) : sec.type === 'team' ? (
                  <>
                    <TeamEditor section={sec} users={users}
                      onToggle={(userId) => toggleUser(sec.id, userId)} />
                    <TextField fullWidth multiline rows={3} size="small" sx={{ mt: 1.5 }}
                      label="Genomförandebeskrivning (text under teamet)"
                      value={sec.content || ''}
                      onChange={e => updateSection(sec.id, 'content', e.target.value)} />
                  </>
                ) : sec.type === 'budget' ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Välj vilken budgetversion som ska visas i offerten. Innehållet hämtas live från budgeten — inga manuella uppdateringar behövs.
                    </Typography>
                    {(offer?.budgets || []).length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'warning.main' }}>
                        Ingen budget skapad ännu.{' '}
                        <span
                          style={{ color: '#1565C0', cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigate(`/offers/${offerId}/budget-edit`)}>
                          Skapa budget →
                        </span>
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                          <Select
                            value={sec.budgetId || ''}
                            onChange={e => updateSection(sec.id, 'budgetId', e.target.value)}
                            displayEmpty
                            renderValue={val => {
                              if (!val) return <em style={{ color: '#aaa' }}>– Välj budgetversion –</em>;
                              const b = (offer.budgets || []).find(b => b.id === val);
                              return b?.name || val;
                            }}>
                            <MenuItem value=""><em>– Välj budgetversion –</em></MenuItem>
                            {(offer.budgets || []).map(b => (
                              <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Visningsformat</Typography>
                          <ToggleButtonGroup size="small" exclusive
                            value={sec.budgetDisplay || 'activity'}
                            onChange={(_, v) => v && updateSection(sec.id, 'budgetDisplay', v)}
                            sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 1.5, fontSize: '0.72rem' } }}>
                            <ToggleButton value="activity">Per aktivitet</ToggleButton>
                            <ToggleButton value="role-month">Roll × månad</ToggleButton>
                            <ToggleButton value="role-total">Roll totalt</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <ContentEditor
                    rows={sec.type === 'terms' ? 8 : 5}
                    value={sec.content || ''}
                    onChange={val => updateSection(sec.id, 'content', val)} />
                )}
                {sec.type !== 'pricing' && (
                  <SectionImagePanel
                    section={sec}
                    offerId={offerId}
                    onUpdate={(field, val) => updateSection(sec.id, field, val)}
                  />
                )}
              </AccordionDetails>
              </Accordion>
              )}
            </SortableSection>
          ))}
            </SortableContext>
          </DndContext>

          {/* Budget status banner */}
          {(() => {
            const hasBudget = (offer?.budgets || []).some(b =>
              (b.resources?.length > 0) || (b.activities?.length > 0));
            if (hasBudget) return (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: '#F3E5F5', border: '1px solid #CE93D8', borderRadius: 1, px: 1.5, py: 1 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 16, color: '#9C27B0' }} />
                <Typography variant="body2" sx={{ color: '#6A1B9A', flex: 1, fontSize: '0.8rem' }}>
                  <strong>{(offer.budgets || []).length} budget{(offer.budgets || []).length > 1 ? 'versioner' : 'version'} kopplad.</strong>
                  {' '}Lägg till ett "Budgetsammanställning"-block i offerten för att visa den.
                </Typography>
                <Button size="small" sx={{ color: '#9C27B0', fontSize: '0.72rem', p: 0.5 }}
                  onClick={() => navigate(`/offers/${offerId}/budget-edit`)}>
                  Redigera →
                </Button>
              </Box>
            );
            return (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: '#FFF8F0', border: '1px dashed #FF6B00', borderRadius: 1, px: 1.5, py: 1 }}>
                <AccountBalanceWalletIcon sx={{ fontSize: 16, color: '#FF6B00' }} />
                <Typography variant="body2" sx={{ color: '#E65100', flex: 1, fontSize: '0.8rem' }}>
                  Ingen budget kopplad till denna offert.
                </Typography>
                <Button size="small" variant="outlined"
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate(`/offers/${offerId}/budget-edit`)}
                  sx={{ borderColor: '#FF6B00', color: '#FF6B00', fontSize: '0.72rem',
                    '&:hover': { bgcolor: 'rgba(255,107,0,0.08)' } }}>
                  Koppla budget
                </Button>
              </Box>
            );
          })()}

          {/* Add section */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
            <TextField select size="small" value={addSectionType}
              onChange={e => setAddSectionType(e.target.value)} sx={{ minWidth: 220 }}>
              {SECTION_TYPES.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </TextField>
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={addSection}
              sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
              Lägg till sektion
            </Button>
          </Box>
        </Box>

        {/* ── Drag handle ── */}
        <Box
          onMouseDown={() => { dragging.current = true; document.body.style.cursor = 'col-resize'; }}
          sx={{
            width: 5, flexShrink: 0, cursor: 'col-resize',
            bgcolor: 'divider',
            '&:hover': { bgcolor: '#FF6B00' },
            transition: 'background-color 0.15s',
          }}
        />

        {/* ── Right: Preview ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: '#ccc', p: 3 }}>
          <OfferPreview meta={meta} sections={sections}
            company={companyObj} users={users} logos={companyLogos}
            companyRefs={companyRefs} selectedRefIds={selectedRefIds}
            budgets={offer?.budgets || []} />
        </Box>
      </Box>

      {/* Print-only section */}
      <Box className="print-only">
        <Box ref={printRef}>
          <OfferPreview meta={meta} sections={sections}
            company={companyObj} users={users} logos={companyLogos} printMode
            companyRefs={companyRefs} selectedRefIds={selectedRefIds}
            budgets={offer?.budgets || []} />
        </Box>
      </Box>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          html, body { margin: 0 !important; padding: 0 !important; background: #f0f0f0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Dialog: välj referensuppdrag */}
      <Dialog open={refPickerOpen}
        onClose={() => { setRefPickerOpen(false); setRefSearch(''); }}
        maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Välj referensuppdrag</Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedRefIds.length} valda · Klicka för att markera/avmarkera
          </Typography>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 1 }}>
          <TextField fullWidth size="small"
            placeholder="Sök på namn, nummer eller kund..."
            value={refSearch}
            onChange={e => setRefSearch(e.target.value)} />
        </Box>
        <DialogContent dividers sx={{ p: 2 }}>
          {companyRefs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              Inga referensuppdrag registrerade ännu.
            </Typography>
          ) : (() => {
            const q = refSearch.toLowerCase();
            const visible = companyRefs.filter(r =>
              !q ||
              (r.name || '').toLowerCase().includes(q) ||
              (r.projectNumber || '').toLowerCase().includes(q) ||
              (r.client || '').toLowerCase().includes(q)
            );
            if (visible.length === 0) return (
              <Typography variant="body2" color="text.secondary"
                sx={{ py: 2, textAlign: 'center' }}>
                Inga träffar.
              </Typography>
            );
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {visible.map(ref => {
                  const selected = selectedRefIds.includes(ref.id);
                  return (
                    <Box key={ref.id}
                      onClick={() => {
                        setSelectedRefIds(prev =>
                          selected ? prev.filter(id => id !== ref.id) : [...prev, ref.id]
                        );
                        markDirty();
                      }}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 2, p: 2,
                        borderRadius: 1, cursor: 'pointer', border: '2px solid',
                        borderColor: selected ? '#FF6B00' : 'divider',
                        bgcolor: selected ? '#FFF3E0' : 'background.paper',
                        '&:hover': { borderColor: '#FF6B00', bgcolor: '#FFF8F0' },
                        transition: 'all 0.15s',
                      }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: selected ? '#FF6B00' : 'transparent',
                        border: '2px solid',
                        borderColor: selected ? '#FF6B00' : '#ccc',
                        color: selected ? 'white' : 'transparent',
                        fontSize: '0.75rem', fontWeight: 700,
                      }}>
                        ✓
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                          <Typography variant="body2" fontWeight={700}>{ref.name}</Typography>
                          {ref.projectNumber && (
                            <Chip label={'#' + ref.projectNumber} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f0f0f0' }} />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          {ref.client && (
                            <Typography variant="caption" color="text.secondary">{ref.client}</Typography>
                          )}
                          {(ref.startDate || ref.endDate) && (
                            <Typography variant="caption" color="text.secondary">
                              {ref.startDate}{ref.endDate ? ' – ' + ref.endDate : ''}
                            </Typography>
                          )}
                        </Box>
                        {ref.projectDescription && (
                          <Typography variant="caption" color="text.secondary"
                            sx={{ display: '-webkit-box', mt: 0.5,
                              overflow: 'hidden', WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical' }}>
                            {ref.projectDescription}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {selectedRefIds.length} referensuppdrag valda
          </Typography>
          <Button onClick={() => { setRefPickerOpen(false); setRefSearch(''); }}
            variant="contained"
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
            Klar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: skapa nytt företag */}
      <Dialog open={newCompanyDialog} onClose={() => setNewCompanyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nytt företag</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField autoFocus fullWidth label="Företagsnamn" size="small"
              value={newCompanyName}
              onChange={e => setNewCompanyName(e.target.value)} />
            <TextField fullWidth label="Kontaktperson (valfritt)" size="small"
              value={newCompanyContact}
              onChange={e => setNewCompanyContact(e.target.value)} />
            <TextField fullWidth label="E-post (valfritt)" size="small"
              value={newCompanyEmail}
              onChange={e => setNewCompanyEmail(e.target.value)} />
            <TextField fullWidth label="Telefon (valfritt)" size="small"
              value={newCompanyPhone}
              onChange={e => setNewCompanyPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateCompany()} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCompanyDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateCompany}
            disabled={!newCompanyName.trim()}
            sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
            Skapa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: välj från DokumentN */}
      <Dialog open={libPickerOpen} onClose={() => { setLibPickerOpen(false); setLibSearch(''); setLibCatFilter(''); }}
        maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Välj från DokumentN</Typography>
          <Typography variant="caption" color="text.secondary">
            Klicka på ett dokument för att lägga till det som bilaga
          </Typography>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 1 }}>
          <TextField fullWidth size="small" placeholder="Sök dokument..."
            value={libSearch} onChange={e => setLibSearch(e.target.value)} sx={{ mb: 1 }} />
          {libCategories.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Chip label="Alla" size="small" clickable
                variant={libCatFilter === '' ? 'filled' : 'outlined'}
                onClick={() => setLibCatFilter('')}
                sx={libCatFilter === '' ? { bgcolor: '#FF6B00', color: 'white', fontWeight: 700 } : {}} />
              {libCategories.map(cat => (
                <Chip key={cat} label={cat} size="small" clickable
                  variant={libCatFilter === cat ? 'filled' : 'outlined'}
                  onClick={() => setLibCatFilter(c => c === cat ? '' : cat)}
                  sx={libCatFilter === cat ? { bgcolor: '#FF6B00', color: 'white', fontWeight: 700 } : {}} />
              ))}
            </Box>
          )}
        </Box>
        <DialogContent dividers sx={{ p: 2 }}>
          {libDocs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Inga dokument i biblioteket
            </Typography>
          ) : (() => {
            const q = libSearch.toLowerCase();
            const visible = libDocs.filter(d =>
              (!q || (d.name || '').toLowerCase().includes(q) || (d.category || '').toLowerCase().includes(q)) &&
              (!libCatFilter || d.category === libCatFilter));
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {visible.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Inget dokument hittades
                    </Typography>
                    <Button size="small" variant="outlined" startIcon={<CloudUploadIcon />}
                      onClick={() => { setLibUploadName(libSearch); setLibUploadCat(libCatFilter); setLibUploadDialog(true); }}
                      sx={{ borderColor: '#FF6B00', color: '#FF6B00' }}>
                      Ladda upp till biblioteket
                    </Button>
                  </Box>
                ) : visible.map(d => {
                  const already = attachments.some(a => a.libDocId === d.id);
                  return (
                    <Box key={d.id}
                      onClick={() => { if (!already) { handleAttachFromLibrary(d); setLibPickerOpen(false); setLibSearch(''); setLibCatFilter(''); } }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                        border: '1px solid', borderRadius: 1, cursor: already ? 'default' : 'pointer',
                        borderColor: already ? '#FF6B00' : 'divider',
                        bgcolor: already ? '#FFF8F0' : 'background.paper',
                        opacity: already ? 0.7 : 1,
                        '&:hover': !already ? { borderColor: '#FF6B00', bgcolor: '#FFF8F0' } : {},
                      }}>
                      <InsertDriveFileIcon sx={{ color: '#FF6B00', fontSize: 20, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{d.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{d.category}</Typography>
                      </Box>
                      {already && <Chip label="Tillagd" size="small" sx={{ fontSize: '0.6rem', bgcolor: '#FFF3E0', color: '#FF6B00' }} />}
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button size="small" startIcon={<CloudUploadIcon />}
            onClick={() => { setLibUploadName(''); setLibUploadCat(libCatFilter); setLibUploadDialog(true); }}
            sx={{ color: '#FF6B00' }}>
            Ladda upp nytt
          </Button>
          <Button onClick={() => { setLibPickerOpen(false); setLibSearch(''); setLibCatFilter(''); }}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: ladda upp till biblioteket */}
      <Dialog open={libUploadDialog} onClose={() => { setLibUploadDialog(false); setLibUploadFile(null); setLibUploadName(''); setLibUploadCat(''); setLibUploadProgress(null); }}
        maxWidth="xs" fullWidth>
        <DialogTitle>Ladda upp till DokumentN</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}
              sx={{ justifyContent: 'flex-start' }}>
              {libUploadFile ? libUploadFile.name : 'Välj fil'}
              <input type="file" hidden accept=".pdf,.doc,.docx,.xlsx,.xls,.ppt,.pptx,.png,.jpg"
                onChange={e => {
                  const f = e.target.files[0];
                  if (f) { setLibUploadFile(f); if (!libUploadName) setLibUploadName(f.name.replace(/\.[^.]+$/, '')); }
                  e.target.value = '';
                }} />
            </Button>
            <TextField size="small" fullWidth label="Namn i biblioteket" value={libUploadName}
              onChange={e => setLibUploadName(e.target.value)} />
            <TextField select size="small" fullWidth label="Kategori" value={libUploadCat}
              onChange={e => setLibUploadCat(e.target.value)}>
              {(libCategories.length > 0 ? libCategories : ['Övrigt']).map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
            {libUploadProgress !== null && (
              <LinearProgress variant="determinate" value={libUploadProgress} sx={{ borderRadius: 1 }} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLibUploadDialog(false); setLibUploadFile(null); setLibUploadName(''); setLibUploadCat(''); setLibUploadProgress(null); }}>Avbryt</Button>
          <Button variant="contained" disabled={!libUploadFile || !libUploadName.trim() || libUploadProgress !== null}
            onClick={handleUploadToLibrary}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Ladda upp & bifoga
          </Button>
        </DialogActions>
      </Dialog>
    </Box>

    <Snackbar
      open={copySnack} autoHideDuration={2500} onClose={() => setCopySnack(false)}
      message="Länk kopierad till urklipp"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    />
    </>
  );
}

/* ─── Section image panel ────────────────────────────────────────────────── */
function SectionImagePanel({ section, offerId, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);
  const images = section.images || [];
  const layout = section.imageLayout || '2col';

  async function uploadFile(file) {
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const imgId = Date.now().toString(36) + Math.random().toString(36).slice(2);
      const sRef = storageRef(storage,
        `offers/${offerId}/sections/${section.id}/${imgId}`);
      await uploadBytesResumable(sRef, file);
      const url = await getDownloadURL(sRef);
      onUpdate('images', [...images, { id: imgId, url, caption: '' }]);
    } finally {
      setUploading(false);
    }
  }

  async function removeImage(img) {
    try {
      await deleteObject(storageRef(storage,
        `offers/${offerId}/sections/${section.id}/${img.id}`));
    } catch {}
    onUpdate('images', images.filter(i => i.id !== img.id));
  }

  function handlePaste(e) {
    const items = Array.from(e.clipboardData?.items || []);
    const imgItem = items.find(it => it.type.startsWith('image/'));
    if (imgItem) { e.preventDefault(); uploadFile(imgItem.getAsFile()); }
  }

  function handleDrop(e) {
    e.preventDefault();
    Array.from(e.dataTransfer.files).forEach(uploadFile);
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" fontWeight="bold" color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Bilder
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup value={layout} exclusive size="small"
            onChange={(_, v) => v && onUpdate('imageLayout', v)}>
            <ToggleButton value="1col"
              sx={{ fontSize: '0.62rem', py: 0.25, px: 1, lineHeight: 1.2 }}>
              1 kolumn
            </ToggleButton>
            <ToggleButton value="2col"
              sx={{ fontSize: '0.62rem', py: 0.25, px: 1, lineHeight: 1.2 }}>
              2 kolumner
            </ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" startIcon={<ImageIcon sx={{ fontSize: 16 }} />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            sx={{ fontSize: '0.72rem', py: 0.4 }}>
            Lägg till bild
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple
            style={{ display: 'none' }}
            onChange={e => { Array.from(e.target.files).forEach(uploadFile); e.target.value = ''; }} />
        </Box>
      </Box>

      {images.length === 0 ? (
        <Box ref={dropRef}
          onPaste={handlePaste} onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          tabIndex={0}
          sx={{
            border: '2px dashed', borderColor: 'divider', borderRadius: 1,
            p: 2, textAlign: 'center', cursor: 'default',
            outline: 'none',
            '&:focus': { borderColor: '#FF6B00' },
          }}>
          <Typography variant="caption" color="text.disabled">
            Klicka "Lägg till bild", dra & släpp, eller klistra in (Ctrl+V) en bild
          </Typography>
        </Box>
      ) : (
        <Box onPaste={handlePaste} tabIndex={0} sx={{ outline: 'none' }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: layout === '2col' ? '1fr 1fr' : '1fr',
            gap: 1,
          }}>
            {images.map(img => (
              <Box key={img.id} sx={{ position: 'relative', borderRadius: 1,
                overflow: 'hidden', bgcolor: '#f5f5f5' }}>
                <Box component="img" src={img.url} sx={{
                  width: '100%', display: 'block',
                  maxHeight: 200, objectFit: 'cover',
                }} />
                <IconButton size="small" onClick={() => removeImage(img)}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    bgcolor: 'rgba(0,0,0,0.55)', color: 'white', p: 0.3,
                    '&:hover': { bgcolor: 'rgba(180,0,0,0.8)' },
                  }}>
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
          {uploading && (
            <LinearProgress sx={{ mt: 1, borderRadius: 1,
              '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
          )}
          <Box onDrop={handleDrop} onDragOver={e => e.preventDefault()}
            sx={{ mt: 1, borderRadius: 1, border: '1px dashed',
              borderColor: 'divider', p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              Dra & släpp fler bilder hit
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/* ─── Pricing editor ─────────────────────────────────────────────────────── */
function PricingEditor({ section, onUpdateSection, onUpdateRow, onAddRow, onRemoveRow }) {
  const isHourly = section.pricingMode !== 'fixed';
  return (
    <Box>
      <ToggleButtonGroup size="small" exclusive
        value={section.pricingMode || 'hourly'}
        onChange={(_, v) => v && onUpdateSection('pricingMode', v)}
        sx={{ mb: 1.5 }}>
        <ToggleButton value="hourly"
          sx={{ '&.Mui-selected': { bgcolor: '#FF6B00', color: 'white' } }}>
          Löpande (tim)
        </ToggleButton>
        <ToggleButton value="fixed"
          sx={{ '&.Mui-selected': { bgcolor: '#FF6B00', color: 'white' } }}>
          Fastpris
        </ToggleButton>
      </ToggleButtonGroup>

      {isHourly ? (
        <>
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, width: '55%' }}>
                  Roll / Beskrivning
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, width: '30%' }}>
                  SEK/tim
                </TableCell>
                <TableCell sx={{ width: 32 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(section.rows || []).map(row => (
                <TableRow key={row.id}>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField size="small" fullWidth variant="standard"
                      value={row.description}
                      onChange={e => onUpdateRow(row.id, 'description', e.target.value)}
                      placeholder="t.ex. Expertstöd" />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField size="small" fullWidth variant="standard" type="number"
                      value={row.price}
                      onChange={e => onUpdateRow(row.id, 'price', e.target.value)}
                      placeholder="1295" />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <IconButton size="small" onClick={() => onRemoveRow(row.id)}
                      sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button size="small" startIcon={<AddIcon />} onClick={onAddRow}
            sx={{ color: '#FF6B00', mb: 1.5 }}>
            Lägg till roll
          </Button>
          {isHourly && (
            <TextField fullWidth label="Budget-notering" size="small" sx={{ mb: 1 }}
              value={section.budgetNote || ''}
              onChange={e => onUpdateSection('budgetNote', e.target.value)}
              placeholder="t.ex. Budget bifogas denna offert." />
          )}
          <TextField fullWidth label="Faktureringsvillkor" size="small"
            value={section.paymentTerms || ''}
            onChange={e => onUpdateSection('paymentTerms', e.target.value)} />
        </>
      ) : (
        <>
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, width: '55%' }}>
                  Beskrivning
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.5, width: '30%' }}>
                  Fastpris (SEK)
                </TableCell>
                <TableCell sx={{ width: 32 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(section.rows || []).map(row => (
                <TableRow key={row.id}>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField size="small" fullWidth variant="standard"
                      value={row.description}
                      onChange={e => onUpdateRow(row.id, 'description', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <TextField size="small" fullWidth variant="standard" type="number"
                      value={row.price}
                      onChange={e => onUpdateRow(row.id, 'price', e.target.value)} />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <IconButton size="small" onClick={() => onRemoveRow(row.id)}
                      sx={{ opacity: 0.4, '&:hover': { opacity: 1, color: 'error.main' } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button size="small" startIcon={<AddIcon />} onClick={onAddRow}
            sx={{ color: '#FF6B00', mb: 1.5 }}>
            Lägg till rad
          </Button>
          <TextField fullWidth label="Betalningsvillkor & reservationer" size="small"
            multiline rows={3}
            value={section.paymentTerms || ''}
            onChange={e => onUpdateSection('paymentTerms', e.target.value)}
            placeholder="100% av arvodet vid leverans." />
        </>
      )}

      <TextField fullWidth label="Ytterligare text i avsnittet" size="small"
        multiline rows={2} sx={{ mt: 1.5 }}
        value={section.content || ''}
        onChange={e => onUpdateSection('content', e.target.value)}
        placeholder="Genomförandebeskrivning, noteringar..." />
    </Box>
  );
}

/* ─── Team editor ────────────────────────────────────────────────────────── */
function TeamEditor({ section, users, onToggle }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Välj konsulter som ingår:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
        {users.map(u => {
          const sel = (section.userIds || []).includes(u.id);
          return (
            <Chip key={u.id}
              avatar={
                <Avatar sx={{ bgcolor: sel ? 'white' : '#1a1a1a',
                  color: sel ? '#1a1a1a' : 'white', fontSize: '0.65rem' }}>
                  {(u.displayName || u.email || '?')[0].toUpperCase()}
                </Avatar>
              }
              label={u.displayName || u.email}
              size="small"
              onClick={() => onToggle(u.id)}
              variant={sel ? 'filled' : 'outlined'}
              sx={{ bgcolor: sel ? '#1a1a1a' : 'transparent',
                color: sel ? 'white' : 'text.primary', cursor: 'pointer' }} />
          );
        })}
      </Box>
    </Box>
  );
}
